import { useCallback } from "react";

import { AccountStateEnum } from "../../contexts/AuthContext";
import { LocalStorageKeyEnum } from "../../enums/localStorage";
import { WalletEnum, isSdkWalletEnum } from "../../utils/wallet/types";
import { IConnectWalletModalProps, ConnectWalletPageEnum } from "./types";
import { runRegister } from "./runRegister";
import { runDepositOrApprove } from "./runDepositOrApprove";
import { useModalSetup } from "./useModalSetup";
import { useModalPaging } from "./useModalPaging";
import { useModalEffects } from "./useModalEffects";
import { logger } from "../../utils/logger";
import { ToastEnum, ToastStatusEnum } from "../../utils/toast";
import {
  WalletConnectTimeoutError,
  isWalletConnectAborted,
} from "../../utils/wallet/providers/types";

type Ctx = IConnectWalletModalProps &
  ReturnType<typeof useModalSetup> &
  ReturnType<typeof useModalPaging> &
  ReturnType<typeof useModalEffects>;

export function useModalActions(ctx: Ctx) {
  const {
    account, selectedWallet, setIsConnectingToWallet, activate,
    selectedEIP6963Provider, setLoading, getImportedAccountAddress,
    reconnectImportedAccount, setShowImportManuallyModal, updatePageIndex,
    pageIndex, connectWalletInFlightRef, inviteCode, inviteRequired, setPageError,
    lastRegisterSuccessRef, registerInFlightRef, registerSigningKey,
    isRememberMeChecked, addToast, referralData, mutateInviteStatus, t, chainId,
    depositActionInFlightRef, setDepositError, depositReady, useUnifiedVaultFlow,
    isNativeDepositAsset, setUnifiedVaultApprovalChecked, decimals,
    isUnifiedVaultApproved, approveUnifiedVaultUSDC, setShowReadyToDepositMessage,
    l1DepositHelper, getAssetPermitSignature, getTimestamp,
    setApprovedAmountAndSignature, approvalSpender, approveAsset, setAssetAllowance,
    assetToDeposit, depositToUnifiedVault, depositETHOptimismOrArbitrum, depositETH,
    l1Contract, l2Address, depositERC20OptimismOrArbitrum, yieldVaultToggle,
    approvedAmountAndSignature, depositERC20WithPermit, depositERC20,
    setPendingDepositTxURL, mutate, onHideClicked, playSound, page, setChainToSwitch,
    selectedDepositChain, accountSigningKeyState, accountApiKeyState, depositForm,
  } = ctx;

  const connectWallet = useCallback(async () => {
    if (connectWalletInFlightRef.current) {
      return;
    }

    connectWalletInFlightRef.current = true;
    try {
      if (!account) {
        if (selectedWallet) {
          setIsConnectingToWallet(selectedWallet);

          if (selectedWallet !== "manualImport") {
            setLoading(true);
            try {
              // The rdns must travel with the provider. Without it this path --
              // the CTA, reachable whenever a wallet is already selected, e.g.
              // after the user rejects the first prompt and presses Continue --
              // connected an EIP-6963 wallet while activate() left the selection
              // record cleared, so a later reload had nothing to restore and the
              // app fell back to whichever extension won window.ethereum.
              await activate(
                selectedWallet,
                selectedWallet === WalletEnum.EIP6963
                  ? selectedEIP6963Provider?.provider
                  : undefined,
                selectedWallet === WalletEnum.EIP6963
                  ? selectedEIP6963Provider?.info?.rdns
                  : undefined
              );
            } finally {
              setLoading(false);
            }
          } else if (getImportedAccountAddress()) {
            reconnectImportedAccount();
          } else {
            setShowImportManuallyModal(true);
            return;
          }

          setIsConnectingToWallet(undefined);
        }
      }
      updatePageIndex(pageIndex + 1);
    } catch (err) {
      // A cancel is the expected exit and stays quiet. A TIMEOUT is not: it means
      // a wallet SDK failed to answer, and the user is owed an explanation rather
      // than a silently dead button.
      //
      // The comment that used to live here said "SDK modals reject on dismiss by
      // design". That assumption was wrong twice — Para (0b598f9) and Privy — and
      // both times the recovery below never ran, because a promise that never
      // settles reaches no `catch` at all. Settlement is now guaranteed by
      // `usePendingConnect` plus the net in `useWallet.activate`, so this really
      // is reached.
      if (err instanceof WalletConnectTimeoutError) {
        logger.error("[connect] wallet connect timed out", err);
        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: t("wallet_connect_timed_out_title"),
            subheader: t("wallet_connect_timed_out_desc"),
            status: ToastStatusEnum.ERROR,
          },
          8000
        );
      } else if (isWalletConnectAborted(err)) {
        logger.debug("[connect] wallet connect aborted:", err);
      } else {
        // Genuinely unexpected: previously swallowed into console.debug.
        logger.warn("[connect] wallet connect failed:", err);
      }
      // On SDK chains (Privy/Para/DogeOS) the SDK's native modal IS the wallet
      // picker, so our intermediate "Connect <SDK>" step is redundant. Cancelling
      // the SDK modal should close the whole connect flow rather than leave the
      // wrapper stuck on that step. (For web3-react wallets we stay put so the
      // user can pick a different wallet.)
      if (isSdkWalletEnum(selectedWallet)) {
        onHideClicked();
      }
    } finally {
      setIsConnectingToWallet(undefined);
      connectWalletInFlightRef.current = false;
    }
  }, [
    account,
    activate,
    getImportedAccountAddress,
    onHideClicked,
    pageIndex,
    reconnectImportedAccount,
    selectedWallet,
    selectedEIP6963Provider,
    updatePageIndex,
  ]);

  const register = useCallback(
    () =>
      runRegister({
        inviteCode,
        inviteRequired,
        setPageError,
        account,
        lastRegisterSuccessRef,
        registerInFlightRef,
        setLoading,
        registerSigningKey,
        isRememberMeChecked,
        addToast,
        referralData,
        mutateInviteStatus,
        t,
      }),
    [
      registerSigningKey,
      isRememberMeChecked,
      inviteCode,
      inviteRequired,
      addToast,
      referralData,
      mutateInviteStatus,
      account,
      chainId,
      t,
    ]
  );

  const handleDepositOrApprove = useCallback(
    async (data: { amount: string }) => {
      await runDepositOrApprove(data, {
        depositActionInFlightRef,
        setLoading,
        setPageError,
        setDepositError,
        depositReady,
        useUnifiedVaultFlow,
        isNativeDepositAsset,
        setUnifiedVaultApprovalChecked,
        decimals,
        isUnifiedVaultApproved,
        approveUnifiedVaultUSDC,
        setShowReadyToDepositMessage,
        l1DepositHelper,
        getAssetPermitSignature,
        getTimestamp,
        setApprovedAmountAndSignature,
        approvalSpender,
        approveAsset,
        setAssetAllowance,
        chainId,
        t,
        assetToDeposit,
        depositToUnifiedVault,
        depositETHOptimismOrArbitrum,
        depositETH,
        l1Contract,
        l2Address,
        depositERC20OptimismOrArbitrum,
        yieldVaultToggle,
        approvedAmountAndSignature,
        depositERC20WithPermit,
        depositERC20,
        setPendingDepositTxURL,
        mutate,
        onHideClicked,
        addToast,
        playSound,
      });
    },
    [
      depositReady,
      assetToDeposit,
      useUnifiedVaultFlow,
      isNativeDepositAsset,
      approveUnifiedVaultUSDC,
      isUnifiedVaultApproved,
      depositToUnifiedVault,
      l1DepositHelper,
      getAssetPermitSignature,
      approvalSpender,
      getTimestamp,
      approveAsset,
      chainId,
      t,
      decimals,
      depositETHOptimismOrArbitrum,
      depositETH,
      l1Contract?.address,
      l2Address,
      depositERC20OptimismOrArbitrum,
      yieldVaultToggle,
      approvedAmountAndSignature.deadline,
      approvedAmountAndSignature.signature,
      depositERC20WithPermit,
      depositERC20,
      onHideClicked,
      addToast,
      playSound,
      mutate,
    ]
  );

  const onPreviousPage = useCallback(() => {
    setPageError("");
    updatePageIndex(pageIndex - 1);
  }, [pageIndex, updatePageIndex]);

  const onNextPage = useCallback(async () => {
    setPageError("");
    switch (page) {
      case ConnectWalletPageEnum.ONBOARD:
        updatePageIndex(pageIndex + 1);
        if (yieldVaultToggle) {
          window.localStorage.setItem(
            LocalStorageKeyEnum.SHOW_EARN_INTRO,
            "true"
          );
        }
        break;
      case ConnectWalletPageEnum.CONNECT_WALLET:
        await connectWallet();
        break;
      case ConnectWalletPageEnum.SWITCH_NETWORK:
        setLoading(true);
        setChainToSwitch(selectedDepositChain).finally(() => {
          setLoading(false);
        });
        break;
      case ConnectWalletPageEnum.ENABLE_TRADING_SIGN:
        if (
          !inviteRequired &&
          accountSigningKeyState !==
            AccountStateEnum.REQUIRE_REGISTER_SIGNING &&
          accountApiKeyState !== AccountStateEnum.REQUIRE_REGISTER_SIGNING
        ) {
          updatePageIndex(pageIndex + 1);
        } else {
          register();
        }
        break;
      case ConnectWalletPageEnum.DEPOSIT:
        if (chainId !== selectedDepositChain) {
          setChainToSwitch(selectedDepositChain);
        } else {
          depositForm.handleSubmit(handleDepositOrApprove as any)();
        }
        break;
      default:
        break;
    }
  }, [
    accountApiKeyState,
    accountSigningKeyState,
    chainId,
    connectWallet,
    depositForm,
    handleDepositOrApprove,
    inviteRequired,
    page,
    pageIndex,
    register,
    selectedDepositChain,
    setChainToSwitch,
    updatePageIndex,
    yieldVaultToggle,
  ]);

  return {
    connectWallet,
    register,
    handleDepositOrApprove,
    onPreviousPage,
    onNextPage,
  };
}
